
import { item } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { toUInt32 } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { FastSimulation__get_SimulatedTopSheet, RunOutcome, StepIndex } from "../SimTypes.js";
import { fastReduceFData } from "./EvalAlgebraic.js";
import { min, max } from "../../fable_modules/fable-library-js.5.13.0/Double.js";
import { printf, toFail } from "../../fable_modules/fable-library-js.5.13.0/String.js";
import { getTimeMs } from "../../Common/TimeHelpers.js";
import { SimLogKind, record, beginInvocation } from "../SimLog.js";

export const Constants_maxSimulationTime = 10000000000;

export const Constants_numberOfStepsBeforeTimeCheck = 5;

function propagateInputsFromLastStep(step, fastSim) {
    const stepsim = ((step === 0) ? fastSim.MaxArraySize : step) | 0;
    fastSim.FGlobalInputComps.forEach((fc) => {
        const vec = item(0, fc.Outputs);
        if (vec.Width > 32) {
            let dat;
            const this$_1 = vec;
            const step_3 = (stepsim - 1) | 0;
            dat = (this$_1.BigIntSlab[(this$_1.StepBase + step_3)]);
            const this$ = vec;
            this$.BigIntSlab[(this$.StepBase + step)] = dat;
        }
        else {
            let dat_2;
            const this$_3 = vec;
            const step_6 = (stepsim - 1) | 0;
            dat_2 = (this$_3.UInt32Slab[(this$_3.StepBase + step_6)]);
            const this$_2 = vec;
            this$_2.UInt32Slab[(this$_2.StepBase + step)] = dat_2;
        }
    });
}

function setInputstoDefault(fastSim) {
    fastSim.FGlobalInputComps.forEach((fc) => {
        const matchValue = fc.FType;
        if (matchValue.tag === 0) {
            const defaultVal = matchValue.fields[1];
            if (defaultVal == null) {
            }
            else {
                const defaultVal_1 = defaultVal;
                const vec = item(0, fc.Outputs);
                if (vec.Width > 32) {
                    const this$ = vec;
                    this$.BigIntSlab[(this$.StepBase + 0)] = defaultVal_1;
                }
                else {
                    const dat_2 = toUInt32(defaultVal_1) >>> 0;
                    const this$_1 = vec;
                    this$_1.UInt32Slab[(this$_1.StepBase + 0)] = dat_2;
                }
            }
        }
    });
}

function stepSimulation(fs) {
    let step;
    const maxArraySize = fs.MaxArraySize | 0;
    const numStep = (fs.ClockTick + 1) | 0;
    const simStep = (numStep % maxArraySize) | 0;
    step = (new StepIndex(numStep, simStep, (simStep === 0) ? (maxArraySize - 1) : (simStep - 1)));
    propagateInputsFromLastStep(step.SimStep, fs);
    fs.FClockedComps.forEach((fc) => {
        fc.ReduceClocked(step);
    });
    fs.FOrderedComps.forEach((fc_1) => {
        fc_1.ReduceComb(step);
    });
    fs.ClockTick = (step.NumStep | 0);
}

function restartSimulation(fs) {
    let step;
    const maxArraySize = fs.MaxArraySize | 0;
    const simStep = (0 % maxArraySize) | 0;
    step = (new StepIndex(0, simStep, (simStep === 0) ? (maxArraySize - 1) : (simStep - 1)));
    setInputstoDefault(fs);
    fs.FClockedComps.forEach((fc) => {
        fc.ReduceClocked(step);
    });
    fs.FOrderedComps.forEach((fc_1) => {
        fc_1.ReduceComb(step);
    });
    fs.ClockTick = 0;
}

/**
 * Re-evaluates the combinational logic for the given timestep - used if a combinational
 * input has changed
 */
export function runCombinationalLogic(stepNum, fastSim) {
    let step;
    const maxArraySize = fastSim.MaxArraySize | 0;
    const numStep = stepNum | 0;
    const simStep = (numStep % maxArraySize) | 0;
    step = (new StepIndex(numStep, simStep, (simStep === 0) ? (maxArraySize - 1) : (simStep - 1)));
    fastSim.FOrderedComps.forEach((fc) => {
        fc.ReduceComb(step);
    });
}

export function runCombinationalLogicFData(step, fastSim) {
    fastSim.FOrderedComps.forEach((comp) => {
        fastReduceFData(fastSim.MaxArraySize, step, false, comp);
    });
}

const pollWorkTarget = 1000;

function cyclesBetweenClockReads(fs) {
    return max(1, ~~(pollWorkTarget / max(1, fs.FCompsByIndex.length))) | 0;
}

function runFastSimulationCore(timeOut, lastStepNeeded, fs) {
    let budget;
    if (fs.MaxArraySize === 0) {
        toFail(printf("ERROR: can\'t run a fast simulation with 0 length arrays!"));
    }
    if ((fs.ClockTick - lastStepNeeded) >= fs.MaxArraySize) {
        restartSimulation(fs);
    }
    if (fs.ClockTick >= lastStepNeeded) {
        return RunOutcome.RunCompleted;
    }
    else {
        let deadline;
        const option_1 = timeOut;
        deadline = ((option_1 != null) ? ((budget = option_1, getTimeMs() + budget)) : undefined);
        const cyclesPerRead = cyclesBetweenClockReads(fs) | 0;
        let outOfTime = false;
        while (!outOfTime && (fs.ClockTick < lastStepNeeded)) {
            const until = min(lastStepNeeded, fs.ClockTick + cyclesPerRead) | 0;
            while (fs.ClockTick < until) {
                stepSimulation(fs);
            }
            if (deadline != null) {
                const by = deadline;
                outOfTime = (getTimeMs() > by);
            }
        }
        if (fs.ClockTick >= lastStepNeeded) {
            return RunOutcome.RunCompleted;
        }
        else {
            return new RunOutcome(/* RunStoppedAt */ 1, [fs.ClockTick]);
        }
    }
}

/**
 * runFastSimulationCore with its invocation recorded: one SimLog entry per call that advances
 * the clock. The renderer's progress loop is repeated timed calls to this, so each progress
 * update is one record; calls that find nothing to do (the wave viewer re-asking for a tick it
 * already has, on every render) are not recorded, or they would flood the ring.
 */
export function runFastSimulation(timeOut, lastStepNeeded, fs) {
    const fromTick = fs.ClockTick | 0;
    beginInvocation();
    const start = getTimeMs();
    const result = runFastSimulationCore(timeOut, lastStepNeeded, fs);
    if (fs.ClockTick !== fromTick) {
        record(SimLogKind.SimRun, FastSimulation__get_SimulatedTopSheet(fs), fs.FCompsByIndex.length, fromTick, fs.ClockTick, getTimeMs() - start);
    }
    return result;
}

